import { useCallback, useEffect, useMemo, useState } from "react";
import type { InputActionMeta } from "react-select";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { cache, useSWRInfinite } from "swr";
import { useNotifications } from "../components/Notifications/NotificationsContext";
import { endpoints } from "../endpoints";
import type {
  AttributeInput,
  AttributeSchema,
  AttributeValue,
  ListItemSchema,
  PaginatedListItemSchema,
} from "../types/types.PIM";
import { constructInfiniteScrollQuery } from "./paginatedInfiniteScrollQuery";
import { useDebounce } from "./hooks";
import { fetcher } from "./util";

const PAGE_SIZE = 500;
const SEARCH_DEBOUNCE_MS = 400;

export const SELECT_FILTER_OPTION = null;

const CHOICE_INPUT_TYPES = new Set<AttributeInput>([
  "single_select",
  "multi_select",
]);

const LIST_ITEM_QUERY_PARAMS = new URLSearchParams({
  order_by: "asc",
  status: "active",
});

export function attributeNeedsChoices(
  attribute: Pick<AttributeSchema, "input_type" | "list_id">
): boolean {
  return (
    !!attribute.list_id &&
    !!attribute.input_type &&
    CHOICE_INPUT_TYPES.has(attribute.input_type)
  );
}

/**
 * Prefer the lazy `/pim/lists/{list_id}/choices` path unless a reference-entity
 * mapping already provides `valid_values` (including an empty array). Matches the
 * previous ProductCollectionSchema gate: only `undefined` falls through to lazy.
 */
export function shouldUseLazyListChoices(
  attribute: Pick<AttributeSchema, "input_type" | "list_id">,
  {
    hasReferenceEntityMapping = false,
    validValues,
  }: {
    hasReferenceEntityMapping?: boolean;
    validValues?: readonly unknown[] | null;
  } = {}
): boolean {
  return (
    attributeNeedsChoices(attribute) &&
    (!hasReferenceEntityMapping || validValues === undefined)
  );
}

function choiceLabel(value: AttributeValue): string | null {
  return (
    value.enum_variant?.variant ??
    (typeof value.value === "string" ? value.value : null)
  );
}

export function buildAttributeChoices(
  listChoices: string[],
  attribute: Pick<AttributeSchema, "input_type" | "values" | "default_values">
): string[] {
  const selected = [
    ...new Set(
      [...(attribute.values ?? []), ...(attribute.default_values ?? [])]
        .map(choiceLabel)
        .filter((value): value is string => Boolean(value))
    ),
  ];

  const choices = [...new Set([...selected, ...listChoices])];

  if (attribute.input_type === "multi_select") {
    const selectedSet = new Set(selected);
    return choices
      .filter((choice) => !selectedSet.has(choice))
      .sort((a, b) => a.localeCompare(b));
  }

  return choices.sort((a, b) => a.localeCompare(b));
}

/** Clears SWR infinite-scroll cache between tests. */
export function resetListItemsRateLimitState(): void {
  cache.clear();
}

export function buildListCreatableOptionProps({
  t,
  listName,
  optionValues,
  onCreateOption,
  enabled = true,
  excludeValues = [],
}: {
  t: TFunction;
  listName?: string | null;
  optionValues: string[];
  onCreateOption: (value: string) => void;
  enabled?: boolean;
  excludeValues?: string[];
}) {
  if (!enabled) {
    return {};
  }

  const taken = new Set(
    [...optionValues, ...excludeValues].map((value) => value.toLowerCase())
  );

  return {
    isCreatable: true as const,
    onCreateOption,
    createOptionPosition: "first" as const,
    isValidNewOption: (value: string) =>
      value.length > 0 && value.length < 50 && !taken.has(value.toLowerCase()),
    formatCreateLabel: (value: string) =>
      t(`Save "{{value}}" to {{list_name}}`, {
        value,
        list_name: listName,
      }),
  };
}

/**
 * The machinery every lazily-loaded, infinite-scrolling choice dropdown
 * shares: nothing is requested until the menu is first opened, typing is
 * debounced into the endpoint's own `q` (so filtering stays server-side —
 * hence `SELECT_FILTER_OPTION`), and scrolling to the bottom pulls the next
 * page. Any endpoint paginating a `{data, pagination}` list of named items
 * can back it.
 *
 * `url` is that endpoint, or null while the caller isn't ready (ids still
 * loading, feature disabled); `cacheScope` separates the SWR cache entries of
 * two callers paging the same endpoint for different entities.
 */
export function useLazyPaginatedChoices({
  url,
  cacheScope,
  existingParams,
  errorMessage,
  pageSize = PAGE_SIZE,
}: {
  url: string | null;
  cacheScope: string | undefined;
  existingParams?: URLSearchParams;
  errorMessage: string;
  pageSize?: number;
}) {
  const { notifyError } = useNotifications();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch] = useDebounce(searchInput.trim(), SEARCH_DEBOUNCE_MS);

  const baseUrl = url && menuOpen ? url : null;

  const {
    data: pages,
    error: pagesError,
    setSize,
    isValidating,
  } = useSWRInfinite<PaginatedListItemSchema>(
    (index, previousPageData) => {
      if (!baseUrl || !cacheScope) {
        return null;
      }

      const pageUrl = constructInfiniteScrollQuery<ListItemSchema>({
        baseUrl,
        existingParams,
        query: debouncedSearch,
        previousPageData,
        index,
        pageSize,
      });

      if (!pageUrl) {
        return null;
      }

      return [pageUrl, cacheScope];
    },
    (pageUrl: string) => fetcher<PaginatedListItemSchema>(pageUrl)
  );

  /**
   * swr@0.3 serializes a null key to the shared `""` cache slot, so an idle
   * hook reads back whatever bookkeeping another idle `useSWRInfinite` last
   * wrote there — surfacing as a phantom `error` before the menu is ever
   * opened. Only trust the result while a page is actually keyed.
   */
  const data = baseUrl ? pages : undefined;
  const error = baseUrl ? pagesError : undefined;

  useEffect(() => {
    if (!error) {
      return;
    }
    notifyError(errorMessage, { error });
  }, [error, errorMessage, notifyError]);

  const loadedChoices = useMemo(() => {
    if (!menuOpen || !data?.length) {
      return [];
    }
    return data.flatMap((page) => page.data.map((item) => item.name));
  }, [data, menuOpen]);

  const hasMore = useMemo(() => {
    if (!data?.length) {
      return false;
    }
    const lastPage = data[data.length - 1];
    const loaded =
      (lastPage.pagination?.offset ?? 0) + (lastPage.data?.length ?? 0);
    const total = lastPage.pagination?.total ?? loaded;
    return loaded < total;
  }, [data]);

  const isLoading = Boolean(baseUrl) && !data && !error;

  const onMenuOpen = useCallback(() => {
    if (!url) {
      return;
    }

    setSearchInput("");
    setMenuOpen(true);
  }, [url]);

  const onMenuScrollToBottom = useCallback(() => {
    if (!hasMore || isValidating) {
      return;
    }
    void setSize((size) => size + 1);
  }, [hasMore, isValidating, setSize]);

  const onInputChange = useCallback(
    (query: string, actionMeta: InputActionMeta) => {
      if (!baseUrl) {
        return;
      }
      if (actionMeta.action === "menu-close") {
        setSearchInput("");
        void setSize(1);
        return;
      }
      if (actionMeta.action === "input-change") {
        setSearchInput(query);
        void setSize(1);
      }
    },
    [baseUrl, setSize]
  );

  return {
    loadedChoices,
    isLoading,
    hasMore,
    inputValue: searchInput,
    filterOption: SELECT_FILTER_OPTION,
    onMenuOpen,
    onMenuScrollToBottom,
    onInputChange,
  };
}

type LazyListBackedAttribute = Pick<
  AttributeSchema,
  "id" | "input_type" | "list_id" | "values" | "default_values"
>;

export function useLazyListBackedChoices(
  tenantId: string | undefined,
  attribute: LazyListBackedAttribute,
  enabled = true
) {
  const { t } = useTranslation();

  const url =
    enabled && tenantId && attribute.list_id
      ? endpoints.v2_tenants_id_or_slug_pim_lists_id_choices(
          tenantId,
          attribute.list_id
        )
      : null;

  const cacheScope =
    tenantId && attribute.list_id && attribute.id
      ? `${tenantId}:${attribute.list_id}:${attribute.id}`
      : undefined;

  const { loadedChoices, ...rest } = useLazyPaginatedChoices({
    url,
    cacheScope,
    existingParams: LIST_ITEM_QUERY_PARAMS,
    errorMessage: t("There was an error loading attribute choices"),
  });

  const choices = useMemo(
    () => buildAttributeChoices(loadedChoices, attribute),
    [attribute, loadedChoices]
  );

  return { choices, ...rest };
}
